/**
 * Client actions: the two things an assistant reply may ask the browser to do.
 *
 * Both are free (a navigation, a prefilled brief), both are re-checked here
 * before anything happens, and each runs **once per message id** and only for a
 * reply to a message this tab sent. A reload, a second tab or an old thread
 * never replays them; the message keeps a line saying what was done, with a
 * way to do it again by hand.
 */

import type { AssistantMessageRead, ClientAction } from '../../types/api';
import { GENERATION_PATH, readGenerationPrefill } from '../generation/generationPrefill';
import { allowedAssistantRoute, assistantRouteLabel } from './assistantContext';

/** A checked action: where to go, with what router state, and how to say it. */
export interface ResolvedClientAction {
  to: string;
  state?: unknown;
  /** Past tense, for the muted line under the message. */
  done: string;
}

/**
 * The action, checked. Null when it fails the check: an `open_page` outside the
 * allow-list, or a prefill `readGenerationPrefill` will not accept.
 */
export function resolveClientAction(action: ClientAction): ResolvedClientAction | null {
  switch (action.type) {
    case 'open_page': {
      const route = allowedAssistantRoute(action.route);
      if (route === null) return null;
      return { to: route, done: `Opened ${assistantRouteLabel(route)} for you` };
    }
    case 'prefill_brief': {
      // The frame is untrusted: a missing body is a refusal, not a crash.
      if (typeof action.prefill !== 'object' || action.prefill === null) return null;
      // Topic-only on purpose: no source_url, so it is the quick-start shape.
      const prefill = readGenerationPrefill({
        topic: action.prefill.topic,
        platform: action.prefill.platform,
        post_type: action.prefill.post_type,
      });
      if (prefill === null) return null;
      return { to: GENERATION_PATH, state: prefill, done: 'Opened a new post for you' };
    }
    default:
      // A type a later backend adds is not something this build knows how to do.
      return null;
  }
}

const SENT_KEY = 'outpost:assistant:sent:v1';
const PERFORMED_KEY = 'outpost:assistant:performed:v1';
/** Ids kept per set: plenty for one tab's session, small enough to store. */
const MAX_IDS = 200;

function readIds(key: string): Set<string> {
  try {
    const raw = window.sessionStorage.getItem(key);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return new Set(
      Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

function writeIds(key: string, ids: Set<string>): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify([...ids].slice(-MAX_IDS)));
  } catch {
    // Storage full or blocked: the in-memory set still holds for this page.
  }
}

/** Per tab, surviving a reload (sessionStorage), so a reply mid-reload still counts. */
const sentHere = readIds(SENT_KEY);
const performed = readIds(PERFORMED_KEY);

/** Notes that this tab sent a user message, so its reply may act here. */
export function markSentHere(messageId: string): void {
  sentHere.add(messageId);
  writeIds(SENT_KEY, sentHere);
}

/**
 * Claims a reply's actions for this tab: true exactly once per message id, and
 * only for a reply to something sent from here.
 */
export function claimClientActions(message: AssistantMessageRead): boolean {
  if (message.role !== 'assistant' || message.client_actions.length === 0) return false;
  if (message.reply_to === null || !sentHere.has(message.reply_to)) return false;
  if (performed.has(message.id)) return false;
  performed.add(message.id);
  writeIds(PERFORMED_KEY, performed);
  return true;
}
